// Merchant inventory CRUD.
//
// Lists own items grouped by category, with an inline "available" toggle,
// edit/delete row actions, and an Add modal at top-right.
//
// Real S3 image upload lands in Phase 1.5. Until then the icon picker
// uses a small whitelist of lucide names so merchants can at least
// pick something visual.

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Plus, Pencil, Trash2, Loader2, Upload, Image as ImageIcon, X, LayoutGrid } from 'lucide-react';
import { toast } from 'sonner';
import {
  catalog, merchantItems, uploadImage, type MerchantItem, type MerchantItemInput, type StockState,
  type ServiceCategory } from '../../lib/api';
import { PortalShell } from '../../components/shared/PortalShell';
import { DataTable, type Column } from '../../components/shared/DataTable';
import { iconFor } from '../../lib/icons';
import { formatHtgMinor } from '../../lib/money';
import { Button } from '../../components/stripe-ui/Button';
import { CatalogBrowser } from './CatalogBrowser';
import { Modal } from '../../components/shared/Modal';
import { MERCHANT_NAV } from '../_navs';
import { useMerchantNav } from '../../lib/pendingAction';


// Icon names recognized by lib/icons.ts. Keep narrow so the picker is short.
const ICON_CHOICES = [
  'Wheat', 'Droplet', 'Candy', 'Bean', 'EggFried', 'Utensils', 'CircleDot',
  'Onion', 'Sprout', 'Banana', 'CupSoda', 'Snowflake', 'Coffee',
  'Pill', 'Beaker', 'SprayCan', 'BandageIcon',
];

// Canonical set of units a merchant can stock by. Free-text invites
// drift ("Lb." vs "lbs" vs "1 lb"); the dropdown keeps the storefront
// + cart math tidy. "Other" routed to free-text in a follow-up.
const UNIT_CHOICES = [
  { value: 'each',   label: 'each' },
  { value: 'pair',   label: 'pair' },
  { value: 'pack',   label: 'pack' },
  { value: 'dozen',  label: 'dozen' },
  { value: 'box',    label: 'box' },
  { value: 'bag',    label: 'bag' },
  { value: 'bottle', label: 'bottle' },
  { value: 'tin',    label: 'tin / can' },
  { value: 'tube',   label: 'tube' },
  { value: 'loaf',   label: 'loaf' },
  { value: 'bunch',  label: 'bunch' },
  // Weight
  { value: '1 lb',   label: '1 lb' },
  { value: '2 lb',   label: '2 lb' },
  { value: '5 lb',   label: '5 lb' },
  { value: '10 lb',  label: '10 lb' },
  { value: '1 kg',   label: '1 kg' },
  // Volume
  { value: '250 ml', label: '250 ml' },
  { value: '500 ml', label: '500 ml' },
  { value: '1 L',    label: '1 L' },
  { value: '2 L',    label: '2 L' },
  { value: '1 gal',  label: '1 gal' },
  { value: '5 gal',  label: '5 gal' },
  // Count
  { value: '12 ct',  label: '12 ct' },
  { value: '20 ct',  label: '20 ct' },
  { value: '30 ct',  label: '30 ct' },
];

export function InventoryPage() {
  const qc = useQueryClient();
  const itemsQ = useQuery({ queryKey: ['merchant', 'items'], queryFn: merchantItems.listOwn });
  const catsQ  = useQuery({ queryKey: ['catalog', 'categories'], queryFn: catalog.categories });

  const [editing, setEditing] = useState<MerchantItem | null>(null);
  const [adding, setAdding]   = useState(false);
  const [stockFilter, setStockFilter] = useState('all');
  const [browsing, setBrowsing] = useState(false);

  // Group items by category for the table layout.
  const byCategory = useMemo(() => {
    const map = new Map<number, { category: ServiceCategory; items: MerchantItem[] }>();
    for (const it of itemsQ.data ?? []) {
      const cat = (catsQ.data ?? []).find((c) => c.id === it.category_id);
      if (!cat) continue;
      if (!map.has(cat.id)) map.set(cat.id, { category: cat, items: [] });
      map.get(cat.id)!.items.push(it);
    }
    return Array.from(map.values()).sort((a, b) => a.category.label.localeCompare(b.category.label));
  }, [itemsQ.data, catsQ.data]);

  // Inline availability toggle.
  // Sets stock_state; the server keeps `available` in step, so the
  // storefront never shows something that cannot be fulfilled.
  const stockMut = useMutation({
    mutationFn: ({ id, stock_state }: { id: number; stock_state: StockState }) =>
      merchantItems.update(id, { stock_state }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['merchant', 'items'] }),
    onError: () => toast.error('Could not update stock') });

  const deleteMut = useMutation({
    mutationFn: (id: number) => merchantItems.remove(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['merchant', 'items'] }); toast.success('Item removed'); },
    onError: () => toast.error('Could not delete item') });

  // Home carries a badge when this role owes an action, so it stays
  // visible from any screen.
  const nav = useMerchantNav(MERCHANT_NAV);

  // Flattened rather than one table per category: the design specifies a
  // single result count, and grouping made that impossible. Category becomes
  // a column instead.
  const allItems = useMemo(
    () => byCategory.flatMap(({ category, items }) => items.map((it) => ({ ...it, _cat: category.label }))),
    [byCategory]);

  const visibleItems = useMemo(() => {
    if (stockFilter === 'all') return allItems;
    return allItems.filter((it) => (it.stock_state ?? (it.available ? 'in_stock' : 'out')) === stockFilter);
  }, [allItems, stockFilter]);

  const columns: Column<typeof allItems[number]>[] = [
    {
      key: 'item', header: 'Item', type: 'node', width: 'minmax(0,1.6fr)',
      value: (it) => {
        const Icon = iconFor(it.system_icon);
        return (
          <span className="flex items-center gap-3">
            <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-600">
              <Icon className="h-4 w-4" aria-hidden="true" />
            </span>
            <span className="min-w-0">
              <span className="block truncate text-[13.5px] font-extrabold text-[var(--color-ink)]">{it.name}</span>
              {it.description && (
                <span className="block truncate text-[11px] text-[var(--color-muted)]">{it.description}</span>
              )}
            </span>
          </span>
        );
      },
    },
    {
      key: 'category', header: 'Category', type: 'text', width: '140px',
      value: (it) => it._cat,
    },
    {
      key: 'unit', header: 'Unit', type: 'muted', width: '110px',
      value: (it) => it.unit,
    },
    {
      key: 'price', header: 'Price', type: 'money', width: '120px',
      value: (it) => formatHtgMinor(it.price_htg_minor),
    },
    {
      key: 'stock', header: 'Stock', type: 'node', width: '200px',
      value: (it) => (
        <StockControl
          item={it}
          onSet={(stock_state) => stockMut.mutate({ id: it.id, stock_state })}
          busy={stockMut.isPending}
        />
      ),
    },
    {
      key: 'actions', header: '', type: 'node', width: '90px',
      value: (it) => (
        <span className="inline-flex items-center gap-1">
          <button
            type="button"
            onClick={() => setEditing(it)}
            className="rounded p-1.5 text-[var(--color-muted)] hover:bg-[var(--color-panel)] hover:text-[var(--color-ink)]"
            title="Edit"
          >
            <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={() => { if (window.confirm(`Remove "${it.name}" from inventory?`)) deleteMut.mutate(it.id); }}
            className="rounded p-1.5 text-[var(--color-muted)] hover:bg-[var(--color-danger-bg)] hover:text-[var(--color-danger-fg)]"
            title="Delete"
          >
            <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        </span>
      ),
    },
  ];

  return (
    <PortalShell
      nav={nav}
      activeId="inventory"
      title="Inventory"
      subtitle="What customers see on your shelf. Prices in HTG."
      actions={
        <div className="flex items-center gap-2">
          <Button tone="outline" onClick={() => setBrowsing(true)}>
            <LayoutGrid className="w-3.5 h-3.5 mr-1" /> Add from catalog
          </Button>
          <Button onClick={() => setAdding(true)}>
            <Plus className="w-3.5 h-3.5 mr-1" /> Add item
          </Button>
        </div>
      }
    >
      <div className="space-y-5">
        {itemsQ.isLoading && (
          <div className="text-center py-16 text-[var(--color-muted)] text-[13px]"><Loader2 className="inline w-4 h-4 animate-spin" /> Loading inventory…</div>
        )}

        <DataTable
          title="Your shelf"
          subtitle="What customers see. Prices in HTG."
          filters={STOCK_FILTERS}
          activeFilter={stockFilter}
          onFilter={setStockFilter}
          columns={columns}
          rows={visibleItems}
          rowKey={(it) => it.id}
          loading={itemsQ.isLoading}
          empty="Nothing on your shelf yet. Add an item or pull one from the catalog."
        />
      </div>

      {(adding || editing) && (
        <ItemEditor
          open
          onClose={() => { setAdding(false); setEditing(null); }}
          existing={editing}
          categories={catsQ.data ?? []}
        />
      )}

      {browsing && <CatalogBrowser onClose={() => setBrowsing(false)} />}
    </PortalShell>
  );
}

// ── Add/Edit modal ─────────────────────────────────────────

function ItemEditor({
  open, onClose, existing, categories }: {
  open: boolean;
  onClose: () => void;
  existing: MerchantItem | null;
  categories: ServiceCategory[];
}) {
  const qc = useQueryClient();
  const isEdit = !!existing;
  const cartCats = categories.filter((c) => c.mode === 'cart');

  const [categoryId, setCategoryId] = useState<number | ''>(existing?.category_id ?? (cartCats[0]?.id ?? ''));
  const [name,    setName]          = useState(existing?.name ?? '');
  // Snap free-text legacy units to dropdown choice; fall through to 'each'.
  const [unit,    setUnit]          = useState(() => {
    const initial = existing?.unit ?? 'each';
    return UNIT_CHOICES.some((u) => u.value === initial) ? initial : 'each';
  });
  // Price stored as HTG (gourdes, not minor) in the form; we ×100 on save.
  const [priceHtg, setPriceHtg]     = useState(existing ? String(existing.price_htg_minor / 100) : '');
  const [icon,    setIcon]          = useState(existing?.system_icon ?? 'ShoppingBag');
  const [description, setDescription] = useState(existing?.description ?? '');
  const [available, setAvailable]   = useState(existing?.available ?? true);
  // image_key is the R2 object key; imageUrl is the rendered preview URL.
  // On save we persist image_key; the merchant detail / cart UIs render
  // image_key via the public URL (R2 custom domain configured server-side).
  const [imageKey, setImageKey]     = useState<string | null>(existing?.image_key ?? null);
  const [imageUrl, setImageUrl]     = useState<string | null>(null);
  const [uploading, setUploading]   = useState(false);

  const onPickFile = async (file: File | null) => {
    if (!file) return;
    setUploading(true);
    try {
      const { key, public_url } = await uploadImage(file, 'merchant_item');
      setImageKey(key);
      setImageUrl(public_url);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const save = useMutation({
    mutationFn: () => {
      const input: MerchantItemInput = {
        category_id:     Number(categoryId),
        name:            name.trim(),
        unit:            unit || 'each',
        price_htg_minor: Math.round(parseFloat(priceHtg || '0') * 100),
        system_icon:     icon || null,
        image_key:       imageKey,
        description:     description.trim() || null,
        available };
      if (isEdit && existing) return merchantItems.update(existing.id, input);
      return merchantItems.create(input);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['merchant', 'items'] });
      qc.invalidateQueries({ queryKey: ['storefront'] });  // marketing strip refreshes too
      toast.success(isEdit ? 'Item updated' : 'Item added');
      onClose();
    },
    onError: (e: Error) => toast.error(e.message || 'Save failed') });

  const valid = name.trim() && categoryId && parseFloat(priceHtg) > 0;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? `Edit ${existing!.name}` : 'Add an item'}
      width="md"
      footer={
        <>
          <Button tone="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={() => save.mutate()} loading={save.isPending} disabled={!valid || save.isPending}>
            {isEdit ? 'Save changes' : 'Add to inventory'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Labeled label="Category">
          <select value={categoryId} onChange={(e) => setCategoryId(Number(e.target.value))} className={selectCls}>
            <option value="">Select…</option>
            {cartCats.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
        </Labeled>
        <Labeled label="Name">
          <input value={name} onChange={(e) => setName(e.target.value)} className={inputCls} placeholder="e.g. Rice (long grain)" />
        </Labeled>
        <div className="grid grid-cols-2 gap-3">
          <Labeled label="Unit">
            <select value={unit} onChange={(e) => setUnit(e.target.value)} className={selectCls}>
              {UNIT_CHOICES.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
            </select>
          </Labeled>
          <Labeled label="Price (HTG gourdes)" hint="Stored as centimes internally.">
            <input type="number" min="0" step="0.01" value={priceHtg} onChange={(e) => setPriceHtg(e.target.value)} className={inputCls} />
          </Labeled>
        </div>
        <Labeled label="Description (optional)">
          <input value={description} onChange={(e) => setDescription(e.target.value)} className={inputCls} placeholder="A short description shown to shoppers." />
        </Labeled>

        {/* Image upload + preview. If image_key is set we render the public_url
            preview; otherwise the icon picker is the visual fallback. */}
        <Labeled label="Photo" hint="JPG / PNG / WebP / GIF · max 8 MB · falls back to the icon below if empty.">
          <div className="flex items-start gap-3">
            <div className="w-24 h-24 rounded-lg bg-[var(--color-panel)] border border-[var(--color-border)] flex items-center justify-center overflow-hidden shrink-0">
              {imageUrl || (imageKey && existing?.image_key === imageKey) ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={imageUrl ?? `${import.meta.env.VITE_R2_PUBLIC_URL ?? ''}/${imageKey}`}
                  alt={name || 'item'}
                  className="w-full h-full object-cover"
                />
              ) : (
                <ImageIcon className="w-6 h-6 text-[var(--color-muted)]" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <label className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-[var(--color-border)] bg-white hover:bg-[var(--color-panel)] cursor-pointer text-[12px] font-semibold text-[var(--color-body)]">
                <Upload className="w-3.5 h-3.5" />
                {uploading ? 'Uploading…' : (imageKey ? 'Replace photo' : 'Upload photo')}
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  className="hidden"
                  disabled={uploading}
                  onChange={(e) => onPickFile(e.target.files?.[0] ?? null)}
                />
              </label>
              {imageKey && (
                <button
                  type="button"
                  onClick={() => { setImageKey(null); setImageUrl(null); }}
                  className="ml-2 inline-flex items-center gap-1 px-3 py-2 rounded-md text-[12px] text-[var(--color-danger-fg)] hover:bg-[var(--color-danger-bg)]"
                >
                  <X className="w-3 h-3" /> Remove
                </button>
              )}
              <p className="mt-1.5 text-[11px] text-[var(--color-secondary-2)] break-all">
                {imageKey ? `Stored at ${imageKey}` : 'No photo — the icon below will be used.'}
              </p>
            </div>
          </div>
        </Labeled>

        <Labeled label="Icon" hint="Used when no photo is uploaded.">
          <div className="grid grid-cols-9 gap-1.5">
            {ICON_CHOICES.map((name) => {
              const Icon = iconFor(name);
              const active = icon === name;
              return (
                <button
                  key={name}
                  type="button"
                  onClick={() => setIcon(name)}
                  className={`aspect-square rounded-md flex items-center justify-center transition-colors ${
                    active ? 'bg-brand-600 text-white' : 'bg-[var(--color-panel)] text-[var(--color-secondary-2)] hover:bg-[var(--color-panel)]'
                  }`}
                  title={name}
                >
                  <Icon className="w-4 h-4" />
                </button>
              );
            })}
          </div>
        </Labeled>
        <label className="flex items-center gap-2 text-[13px] text-[var(--color-body)] mt-2">
          <input type="checkbox" checked={available} onChange={(e) => setAvailable(e.target.checked)} className="rounded" />
          Available for customers to shop
        </label>
      </div>
    </Modal>
  );
}

const inputCls  = 'w-full px-3 py-2 text-[14px] bg-white border border-[var(--color-border)] rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-400';
const selectCls = inputCls + ' appearance-none';

function Labeled({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-wider font-semibold text-[var(--color-secondary-2)]">{label}</span>
      {hint && <span className="text-[11px] text-[var(--color-muted)] ml-1">· {hint}</span>}
      <div className="mt-1">{children}</div>
    </label>
  );
}

/**
 * In stock / Low / Out, in one tap.
 *
 * This was a binary Available/Hidden toggle with storefront-visibility copy
 * ("Click to hide from storefront"). A shopkeeper does not think in
 * visibility, they think in stock — and the most common state, nearly gone,
 * had nowhere to go.
 *
 * Low is deliberately still orderable. It is a restock prompt for the
 * merchant, surfaced on their dashboard, not a signal to customers.
 */
const STOCK_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'in_stock', label: 'In stock' },
  { key: 'low', label: 'Low' },
  { key: 'out', label: 'Out' },
];

const STOCK_OPTIONS: { value: StockState; label: string; on: string }[] = [
  { value: 'in_stock', label: 'In stock', on: 'bg-[var(--color-success-fg)] text-white' },
  { value: 'low',      label: 'Low',      on: 'bg-[var(--color-warning-fg)] text-white' },
  { value: 'out',      label: 'Out',      on: 'bg-[var(--color-danger-fg)] text-white' },
];

function StockControl({
  item, onSet, busy,
}: {
  item: MerchantItem;
  onSet: (s: StockState) => void;
  busy: boolean;
}) {
  // Older rows predate the column; treat them as their boolean implied.
  const current: StockState = item.stock_state ?? (item.available ? 'in_stock' : 'out');

  return (
    <div role="group" aria-label={`Stock for ${item.name}`} className="inline-flex gap-[2px] rounded-lg bg-[var(--color-panel)] p-[2px]">
      {STOCK_OPTIONS.map((o) => {
        const active = current === o.value;
        return (
          <button
            key={o.value}
            type="button"
            disabled={busy || active}
            aria-pressed={active}
            onClick={() => onSet(o.value)}
            className={`rounded-md px-2 py-0.5 text-[11px] font-semibold transition-colors disabled:cursor-default ${
              active ? o.on : 'text-[var(--color-secondary-2)] hover:text-[var(--color-ink)]'
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
